// ─── Table-form value storage ────────────────────────────────────────────────
//
// Table cells live in the same `FormSubmission.values` map as standard fields.
// Each cell is keyed by `${rowId}__${colId}`. Row/column IDs are UUIDs (only
// `[a-z0-9-]`), so the `__` separator never collides and the key is URL-safe —
// it travels through `PATCH /form-submissions/:id/fields/:fieldId` unescaped.
//
// Rows the respondent adds at fill time (when `allowAddRows` is on) are stored
// as a TableRow[] under ADDED_ROWS_KEY, so they survive reloads.

import { extractFormulaRefs, type CalcField } from '@/lib/formula'
import type {
  OosConfig,
  TableCellConfig,
  TableCellType,
  TableColumn,
  TableConfig,
  TableRow,
} from '@/types/cgt'

const SEP = '__'

/** Composite values-map key for one table cell. */
export function cellKey(rowId: string, colId: string): string {
  return `${rowId}${SEP}${colId}`
}

/** Reserved values-map key holding rows added by the respondent. */
export const ADDED_ROWS_KEY = '__tableRows__'

/**
 * Reads respondent-added rows from a submission's values map. An entry without
 * a string id is dropped, as cgt-backend's readAddedRows does, so a malformed
 * value can never become a row with no cell keys.
 */
export function readAddedRows(values: Record<string, unknown>): TableRow[] {
  const raw = values[ADDED_ROWS_KEY]
  return Array.isArray(raw)
    ? (raw as unknown[]).filter(
        (r): r is TableRow => !!r && typeof r === 'object' && typeof (r as TableRow).id === 'string',
      )
    : []
}

/**
 * Does this per-cell override carry anything worth storing?
 *
 * The builder deletes an override the moment it is empty, so a cell the author
 * has touched and then cleared does not linger in the definition. That check has
 * to know about EVERY property a cell can hold, and it silently lost
 * `constantText`: a description cell whose type came from its COLUMN produced
 * an override of `{ constantText: 'the text' }` with no `type` of its own, which
 * read as empty — so the words were discarded the instant they were typed.
 *
 * It lives here, next to the shape it describes, rather than inline in the
 * builder's `.tsx`, because `vitest.config.ts` matches only `src/**\/*.test.ts`
 * and a predicate in a component has no coverage at all.
 */
export function isEmptyCellConfig(cell: TableCellConfig): boolean {
  return (
    !cell.type &&
    !cell.formula &&
    !cell.displayFormat &&
    !cell.anchored &&
    !cell.constantText &&
    !(cell.options && cell.options.length) &&
    // A cell whose only override is its Field Library link is that field —
    // dropping it would detach whatever reads the field (Split and Pool's dose)
    // silently.
    !cell.libraryFieldId &&
    // A cell marked not applicable takes no value: that IS the override.
    !cell.disabled &&
    // A cell's own specification overrides its column's.
    !cell.oos &&
    // The KEY is the override, so `lisStage: null` ("no stage", overriding a
    // staged column) is not empty and must survive.
    !Object.prototype.hasOwnProperty.call(cell, 'lisStage') &&
    !Object.prototype.hasOwnProperty.call(cell, 'lisTestCode')
  )
}

// ─── Per-cell type resolution ────────────────────────────────────────────────

export interface ResolvedCell {
  type: TableCellType
  options?: string[]
  formula?: string
  displayFormat?: 'number' | 'decimal2' | 'decimal3' | 'date' | 'datetime'
  /** `description` cells only — the fixed text the author wrote for THIS cell. */
  constantText?: string
  /** Marked not applicable by the author: a shaded cell that takes no value. */
  disabled: boolean
}

/**
 * Resolves a cell's effective config: the per-cell override if present, falling
 * back to the column default for type and dropdown options.
 */
export function resolveCell(config: TableConfig, rowId: string, col: TableColumn): ResolvedCell {
  const override = config.cells?.[cellKey(rowId, col.id)]
  return {
    type: override?.type ?? col.columnType,
    options: override?.options ?? col.options,
    formula: override?.formula,
    displayFormat: override?.displayFormat,
    // Per cell with no column-level fallback: a description column whose rows
    // all said the same sentence would be one sentence repeated down the table.
    constantText: override?.constantText,
    disabled: override?.disabled === true,
  }
}

/**
 * Every calculated cell in a table that has a formula, by RESOLVED type — so a
 * column defaulting to calculated counts, and a formula left behind on a cell
 * that is no longer calculated does not. A cell marked not applicable is
 * skipped: it takes no value, computed or entered (as cgt-backend's
 * calculatedCellNodes).
 */
function calculatedCells(config: TableConfig | undefined): { key: string; formula: string }[] {
  if (!config) return []
  const cells: { key: string; formula: string }[] = []
  for (const row of config.rows) {
    for (const col of config.columns) {
      const cell = resolveCell(config, row.id, col)
      if (!cell.disabled && cell.type === 'calculated' && cell.formula) cells.push({ key: cellKey(row.id, col.id), formula: cell.formula })
    }
  }
  return cells
}

/** The formula of every calculated cell in a table (see `calculatedCells`). */
export function calculatedCellFormulas(config: TableConfig | undefined): string[] {
  return calculatedCells(config).map(c => c.formula)
}

/**
 * Every calculated cell on a form, shaped as a `CalcField` keyed by its cell
 * key, so `recomputeCalculated` can fill their results into a values map in
 * dependency order. That is what lets one cell's formula read another
 * calculated cell — in its own table or another — instead of a blank.
 */
export function calculatedCellNodes(
  fields: { fieldType?: string; tableConfig?: TableConfig }[],
): CalcField[] {
  return fields.flatMap(f =>
    f.fieldType === 'table'
      ? calculatedCells(f.tableConfig).map(c => ({
          id: c.key,
          label: c.key,
          fieldType: 'calculated',
          formula: c.formula,
        }))
      : [],
  )
}

/** Does calculated node `startId` (transitively) reference itself? */
export function formulaReachesItself(nodes: CalcField[], startId: string): boolean {
  return formulaReaches(nodes, startId, startId)
}

/**
 * Does node `startId`'s formula (transitively, through other nodes) reference
 * `targetId`? A picker uses it to leave out a calculated cell that already
 * reads the field being edited, which would close a loop.
 */
export function formulaReaches(nodes: CalcField[], startId: string, targetId: string): boolean {
  const byId = new Map(nodes.map(n => [n.id, n]))
  const seen = new Set<string>()
  const stack = extractFormulaRefs(byId.get(startId)?.formula ?? '')
  while (stack.length > 0) {
    const id = stack.pop()!
    if (id === targetId) return true
    if (seen.has(id)) continue
    seen.add(id)
    stack.push(...extractFormulaRefs(byId.get(id)?.formula ?? ''))
  }
  return false
}

// ─── Aggregate-formula helpers (for calculated cells) ────────────────────────
//
// The expanded `{a} + {b}` quick totals of a whole-form grid reference only its
// predefined cells: it has no table field id, so it cannot use an aggregate
// token (below), and added rows are dynamic.

/**
 * Can this cell feed a total? Only an enabled NUMBER cell, as cgt-backend's
 * isAggregable. A cell linked to a Field Library number field counts too:
 * linking writes the field's type into the cell's own `type`. Every other type
 * is left out rather than blanking the total, so a free-text first column such
 * as a unit id does not void every row total. Calculated, image and
 * description cells hold no entered value at all.
 */
function isAggregable(config: TableConfig, rowId: string, col: TableColumn): boolean {
  const cell = resolveCell(config, rowId, col)
  return !cell.disabled && cell.type === 'number'
}

/** Cell keys of every aggregable cell in a column (optionally excluding one row). */
export function columnCellKeys(config: TableConfig, colId: string, excludeRowId?: string): string[] {
  const col = config.columns.find(c => c.id === colId)
  if (!col) return []
  return config.rows
    .filter(r => r.id !== excludeRowId && isAggregable(config, r.id, col))
    .map(r => cellKey(r.id, colId))
}

/** Cell keys of every aggregable cell in a row (optionally excluding one column). */
export function rowCellKeys(config: TableConfig, rowId: string, excludeColId?: string): string[] {
  return config.columns
    .filter(c => c.id !== excludeColId && isAggregable(config, rowId, c))
    .map(c => cellKey(rowId, c.id))
}

/**
 * Does this column hold any number cell: by its column type, or by a per-cell
 * override on one of its predefined rows? A column total over a column with
 * none could only ever be blank, so cgt-backend refuses it and the pickers do
 * not offer it.
 */
export function columnHasNumericCells(config: TableConfig | undefined, colId: string): boolean {
  const col = config?.columns.find(c => c.id === colId)
  if (!config || !col) return false
  if (col.columnType === 'number') return true
  return config.rows.some(r => config.cells?.[cellKey(r.id, colId)]?.type === 'number')
}

// ─── Table aggregate tokens (z8nrz7eujb) ─────────────────────────────────────
//
// A calculated field (or cell) can read table cells in two ways:
//   - one cell:   {rowId__colId}, an ordinary values-map key;
//   - an aggregate over a column or row of a TABLE FIELD:
//       {<tableFieldId>#col:<colId>:sum}   {<tableFieldId>#col:<colId>:avg}
//       {<tableFieldId>#row:<rowId>:sum}   {<tableFieldId>#row:<rowId>:avg}
//
// The evaluator grammar does not know about aggregates. The caller computes
// them with `tableAggregateValues` and merges the result into the values map
// under the token key (the text between the braces), so the evaluator reads
// them like any other ref. The token holds no '.', so it never parses as a
// cross-form ref. Neither aggregates nor calculated cells are saved:
// `isTableDerivedKey` names them.
//
// Mirrors cgt-backend src/form-engine/table-cells.ts. Keep the two in step.

export type TableAggregateAxis = 'col' | 'row'
export type TableAggregateOp = 'sum' | 'avg'

export interface TableAggregateRef {
  tableFieldId: string
  axis: TableAggregateAxis
  /** Column id when axis is 'col', row id when axis is 'row'. */
  id: string
  op: TableAggregateOp
}

const AGGREGATE_REF_RE = /^([^#.{}]+)#(col|row):([^:.{}]+):(sum|avg)$/

/** The values-map key (and formula token body) of one aggregate. */
export function aggregateRefKey(
  tableFieldId: string,
  axis: TableAggregateAxis,
  id: string,
  op: TableAggregateOp,
): string {
  return `${tableFieldId}#${axis}:${id}:${op}`
}

/** Parse an aggregate token body, or null when the key is not one. */
export function parseTableAggregateRef(key: string): TableAggregateRef | null {
  const m = AGGREGATE_REF_RE.exec(key)
  if (!m) return null
  return {
    tableFieldId: m[1],
    axis: m[2] as TableAggregateAxis,
    id: m[3],
    op: m[4] as TableAggregateOp,
  }
}

interface TableFieldLike {
  id?: string
  label?: string
  fieldType?: string
  tableConfig?: TableConfig
}

function isNumericLike(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v)
  if (typeof v === 'string') {
    const s = v.trim()
    return s !== '' && Number.isFinite(Number(s))
  }
  return false
}

/**
 * SUM and AVG over number-cell values, with the evaluator's SUM() rules: blank
 * cells are skipped, a filled value that does not parse as a number makes the
 * result null, and no filled cell at all is null. AVG divides by the filled
 * cells only.
 */
function aggregate(raw: unknown[]): { sum: number | null; avg: number | null } {
  let sum = 0
  let count = 0
  for (const v of raw) {
    if (v === undefined || v === null || v === '') continue
    if (!isNumericLike(v)) return { sum: null, avg: null }
    sum += Number(v)
    count++
  }
  return count === 0 ? { sum: null, avg: null } : { sum, avg: sum / count }
}

/**
 * Every column and row aggregate of every table field, keyed by token key.
 *
 * Columns cover the predefined rows plus the rows added at fill time; rows
 * cover every column. Only enabled number cells count (see isAggregable).
 */
export function tableAggregateValues(
  fields: TableFieldLike[],
  values: Record<string, unknown>,
): Record<string, number | null> {
  const out: Record<string, number | null> = {}
  const addedRows = readAddedRows(values)
  for (const field of fields) {
    const config = field.tableConfig
    if (field.fieldType !== 'table' || !field.id || !config || !Array.isArray(config.columns)) continue
    const columns = config.columns
    const rows = [...(config.rows ?? []), ...addedRows]

    for (const col of columns) {
      const cells = rows.filter(r => isAggregable(config, r.id, col)).map(r => values[cellKey(r.id, col.id)])
      const { sum, avg } = aggregate(cells)
      out[aggregateRefKey(field.id, 'col', col.id, 'sum')] = sum
      out[aggregateRefKey(field.id, 'col', col.id, 'avg')] = avg
    }
    for (const row of rows) {
      const cells = columns.filter(c => isAggregable(config, row.id, c)).map(c => values[cellKey(row.id, c.id)])
      const { sum, avg } = aggregate(cells)
      out[aggregateRefKey(field.id, 'row', row.id, 'sum')] = sum
      out[aggregateRefKey(field.id, 'row', row.id, 'avg')] = avg
    }
  }
  return out
}

/**
 * Every ref a formula can hold that names one row or column of a table: its
 * cell keys and, for a table field (non-empty `tableFieldId`), its sum and
 * average tokens. Removing that row or column breaks each of them.
 */
export function tableAxisRefIds(
  tableFieldId: string,
  config: { columns: { id: string }[]; rows: { id: string }[] },
  axis: TableAggregateAxis,
  id: string,
): string[] {
  const keys =
    axis === 'row' ? config.columns.map(c => cellKey(id, c.id)) : config.rows.map(r => cellKey(r.id, id))
  const totals = tableFieldId ? (['sum', 'avg'] as const).map(op => aggregateRefKey(tableFieldId, axis, id, op)) : []
  return [...keys, ...totals]
}

/**
 * Is this key derived from a table rather than entered: an aggregate token, or
 * one of `calculatedCellIds` (the ids of `calculatedCellNodes(fields)`)? Such
 * keys are never saved. Without the id set only aggregate keys are recognised:
 * a cell key alone does not say whether the cell is calculated.
 */
export function isTableDerivedKey(key: string, calculatedCellIds?: ReadonlySet<string>): boolean {
  return parseTableAggregateRef(key) !== null || (calculatedCellIds?.has(key) ?? false)
}

/**
 * A readable name for every table ref a formula on this form can hold: each
 * predefined cell ("Vitals › Weight @ Day 1") and each total
 * ("Sum of Weight (Vitals)", "Average of Day 1 (Vitals)"). Column totals are
 * named only for a column with number cells, the ones cgt-backend accepts, so
 * any other total reads as a broken reference.
 */
export function tableRefLabels(fields: TableFieldLike[]): Record<string, string> {
  const labels: Record<string, string> = {}
  for (const field of fields) {
    const config = field.tableConfig
    if (field.fieldType !== 'table' || !field.id || !config || !Array.isArray(config.columns)) continue
    const table = field.label || 'Table'
    for (const col of config.columns) {
      const colLabel = col.label || 'Column'
      for (const row of config.rows) {
        labels[cellKey(row.id, col.id)] = `${table} › ${colLabel} @ ${row.label || 'Row'}`
      }
      if (columnHasNumericCells(config, col.id)) {
        labels[aggregateRefKey(field.id, 'col', col.id, 'sum')] = `Sum of ${colLabel} (${table})`
        labels[aggregateRefKey(field.id, 'col', col.id, 'avg')] = `Average of ${colLabel} (${table})`
      }
    }
    for (const row of config.rows) {
      const rowLabel = row.label || 'Row'
      labels[aggregateRefKey(field.id, 'row', row.id, 'sum')] = `Sum of ${rowLabel} (${table})`
      labels[aggregateRefKey(field.id, 'row', row.id, 'avg')] = `Average of ${rowLabel} (${table})`
    }
  }
  return labels
}

/**
 * The table field that owns a cell key, and the cell's resolved type.
 *
 * The inverse of `cellKey`, needed wherever a cell has to be treated as a field
 * in its own right — an image cell uploads against its composite key, and the
 * upload guard has to answer "is this key really an image cell on this form?".
 * Returns null for a key that names no column on any table field.
 */
export function findCellByKey(
  fields: { id: string; fieldType?: string; tableConfig?: TableConfig }[],
  key: string,
): { field: { id: string }; type: TableCellType } | null {
  const sep = key.indexOf(SEP)
  if (sep < 0) return null
  const colId = key.slice(sep + SEP.length)
  for (const field of fields) {
    const config = field.tableConfig
    if (field.fieldType !== 'table' || !config) continue
    const col = config.columns.find(c => c.id === colId)
    if (!col) continue
    // The per-cell override is keyed by the whole composite key, so it is a
    // direct lookup — which also means an added row (whose id is minted at fill
    // time and is in no config) still resolves, falling back to the column type.
    return { field, type: config.cells?.[key]?.type ?? col.columnType }
  }
  return null
}

/** Cell types a specification can be set on (z8nrz7eq4h). */
export const OOS_CELL_TYPES: readonly TableCellType[] = ['number', 'text', 'date', 'dropdown', 'checkbox']

/**
 * The specification that grades the cell at `key`, or undefined for none.
 *
 * The cell's own `oos` wins. Otherwise the column's applies, but only while
 * the cell still has the column's type: a column spec of number ranges means
 * nothing to a cell overridden to text. Rows added at fill time have no cell
 * overrides, so they always get the column's spec.
 */
export function effectiveCellOos(config: TableConfig | undefined, key: string): OosConfig | undefined {
  if (!config) return undefined
  const sep = key.indexOf(SEP)
  if (sep < 0) return undefined
  const col = config.columns.find(c => c.id === key.slice(sep + SEP.length))
  if (!col) return undefined
  const override = config.cells?.[key]
  // A cell marked not applicable takes no value, so it has nothing to grade.
  if (override?.disabled) return undefined
  const type = override?.type ?? col.columnType
  if (!OOS_CELL_TYPES.includes(type)) return undefined
  if (override?.oos) return override.oos
  return type === col.columnType ? col.oos : undefined
}

/** `{a} + {b} + {c}` over the given cell keys. */
export function sumFormula(keys: string[]): string {
  return keys.map(k => `{${k}}`).join(' + ')
}

/** `({a} + {b} + {c}) / n` over the given cell keys. */
export function averageFormula(keys: string[]): string {
  if (keys.length === 0) return ''
  if (keys.length === 1) return `{${keys[0]}}`
  return `(${keys.map(k => `{${k}}`).join(' + ')}) / ${keys.length}`
}
